import React from "react";
import { ConfigSchema, ProductionReadiness } from "./configTypes";
import { severityStyle } from "./severity";
import { Pill } from "./StateBadge";

/**
 * The derived production readiness, as the API resolved it.
 *
 * Label, severity and help come from `production_readiness_states` in the
 * schema, exactly as `StateBadge` reads `operational_states`: nothing about
 * what READY or BLOCKED means is decided here. The tooltip carries the
 * blockers the API listed — "Org allocation is Customer PMI" — so the badge
 * answers "why not" without opening the record.
 *
 * Renders nothing for a tractor nobody has marked (UNSET) and for a row from
 * an API that predates the field, so the fleet looks as it did until an admin
 * starts marking tractors.
 */
export const UNSET = "UNSET";

interface ReadinessBadgeProps {
  readiness: ProductionReadiness | null;
  schema: ConfigSchema | null;
  size?: "sm" | "md";
}

const ReadinessBadge: React.FC<ReadinessBadgeProps> = ({
  readiness,
  schema,
  size = "md",
}) => {
  if (!readiness || readiness.value === UNSET) return null;
  const def = schema?.production_readiness_states?.find(
    (s) => s.value === readiness.value,
  );
  const style = severityStyle(def?.severity);
  return (
    <Pill
      $size={size}
      title={
        readiness.blockers.length ? readiness.blockers.join(" · ") : def?.help
      }
      style={{
        background: style.bg,
        color: style.color,
        borderColor: style.border,
      }}
    >
      {def?.label ?? readiness.value}
    </Pill>
  );
};

export default ReadinessBadge;
